import { useMemo } from 'react'

import { useAutoAnimate } from '@formkit/auto-animate/react'
import InfiniteScroll from 'react-infinite-scroller'
import { InView } from 'react-intersection-observer'

import { useDebouncedCallback } from '@bettermode/common/hooks'
import { ChatListItem } from '@tribeplatform/react-components/Messaging'
import {
  ARCHIVED_CHATS_QUERY,
  CHATS_QUERY,
  useChats,
} from '@tribeplatform/react-sdk/hooks'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T } from '../../i18n/components/T.js'

import { EmptyChatListState } from './EmptyChatListState.js'
import { LoadingNewChatsIndicator } from './LoadingNewChatsIndicator.js'
import { LoadMoreLoader } from './LoadMoreLoader.js'

const LIST_REORDER_ANIMATION_DURATION = 200
const LOAD_MORE_DEBOUNCE_TIME = 300

interface Props {
  archived?: boolean
  currentChatId: string
}

export const ChatList = ({ archived = false, currentChatId }: Props) => {
  const [chatListParent] = useAutoAnimate({
    duration: LIST_REORDER_ANIMATION_DURATION,
  })
  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetched,
    isLoading,
    isInitialLoading,
    isFetchingNextPage,
  } = useChats({
    variables: {
      ...(archived ? ARCHIVED_CHATS_QUERY : CHATS_QUERY),
    },
    useInfiniteQueryOptions: {
      /**
       * Pages may produce different result after a refetch.
       * The results must be consolidated and deduplicated manually.
       */
      refetchInterval: false,
    },
  })
  const chatListPages = useMemo(() => {
    return data?.pages ?? []
  }, [data?.pages])

  const items = useMemo(() => {
    return chatListPages.flatMap(({ data }) =>
      data.map(chat => (
        <li key={chat._id} className="p-1">
          <ChatListItem
            chat={chat}
            isActive={chat._id === currentChatId}
            className="cursor-pointer"
          />
        </li>
      )),
    )
  }, [chatListPages, currentChatId])

  const loadMore = useDebouncedCallback((reachedBottom: boolean) => {
    if (reachedBottom && hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage()
    }
  }, LOAD_MORE_DEBOUNCE_TIME)

  const isLoadingChats = !isFetched || isLoading || isFetchingNextPage
  const isLoadingMore = hasNextPage && isFetchingNextPage && !isLoading
  const pageSize = archived ? ARCHIVED_CHATS_QUERY.limit : CHATS_QUERY.limit
  const showHasNoMore =
    !hasNextPage &&
    !isFetchingNextPage &&
    !isLoading &&
    items?.length >= pageSize

  if (!isLoadingChats && items.length === 0) {
    return <EmptyChatListState archived={archived} />
  }

  return (
    <InfiniteScroll
      pageStart={0}
      initialLoad={false}
      threshold={100}
      loadMore={() => {}}
      useWindow={false}
      hasMore={hasNextPage || false}
    >
      <ul ref={chatListParent}>{items}</ul>
      {isLoadingChats && isInitialLoading ? <LoadingNewChatsIndicator /> : null}
      <InView
        as="div"
        onChange={loadMore}
        threshold={0.1}
        className="flex items-center justify-center animate-fade-in"
      >
        {!showHasNoMore && <LoadMoreLoader visible={isLoadingMore} />}
        {showHasNoMore && (
          <Text
            variant="bodySm"
            className="text-content-muted p-2 border-t border-line-muted w-full text-center flex items-center justify-center gap-x-2"
          >
            <T
              id="Messaging.Chats.NoMoreChats"
              defaultMessage="You're all caught up. No more messages!"
            />
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <span>💬</span>
          </Text>
        )}
      </InView>
    </InfiniteScroll>
  )
}
